// Parents' Google sign-in on the app's own address. Google sends the parent back to
// <app>/auth/google with an ID token in the address's #fragment (never sent to any server), the app checks
// the state it made for this sign-in, and gives the token and its nonce to Supabase (signInWithIdToken),
// which checks Google's signature, that the token is for this app's client ID, and the nonce.
// Because Google only ever deals with the app's own address, it can show the app's name on its sign-in
// screen once the branding is verified (with Supabase's address in between, it can't).
export const GOOGLE_RETURN_PATH = '/auth/google';
const GOOGLE_AUTH = 'https://accounts.google.com/o/oauth2/v2/auth';

// Random text that is safe in an address (base64url).
export function randomString(bytes = 32) {
  const a = crypto.getRandomValues(new Uint8Array(bytes));
  let bin = '';
  for (const b of a) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// SHA-256 as hex, the form Supabase compares an ID token's nonce with.
export async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(text)));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

// Google sends people back to one address only (the one in the Google client's settings). A sign-in
// started anywhere else (say the .netlify.app address) starts again from there.
export function googleStartOrigin(currentOrigin, appOrigin) {
  return appOrigin && appOrigin !== currentOrigin ? appOrigin : currentOrigin;
}

// Google's sign-in page, asking only for who the person is (openid email profile) and an ID token back.
export function googleAuthUrl({ clientId, origin, state, hashedNonce }) {
  const u = new URL(GOOGLE_AUTH);
  u.searchParams.set('client_id', clientId);
  u.searchParams.set('redirect_uri', origin + GOOGLE_RETURN_PATH);
  u.searchParams.set('response_type', 'id_token');
  u.searchParams.set('scope', 'openid email profile');
  u.searchParams.set('nonce', hashedNonce);
  u.searchParams.set('state', state);
  u.searchParams.set('prompt', 'select_account');
  return u.href;
}

// What Google put on the address: #id_token=...&state=... or #error=...&state=...
export function parseGoogleReturn(hash) {
  const p = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  return { idToken: p.get('id_token') || '', state: p.get('state') || '', error: p.get('error') || '' };
}

// The page to go to after signing in: a page of this app only (never the return address itself).
export function safeReturnPath(path) {
  if (typeof path !== 'string' || !/^\/(?![\/\\])/.test(path) || path.startsWith(GOOGLE_RETURN_PATH)) return '/';
  return path;
}
